import type { Opportunity } from '../types';

export const opportunities: Opportunity[] = [
  {
    id: 'consulting',
    type: 'opportunity',
    title: 'Build a consulting sprint',
    assetName: 'Consulting sprint',
    description:
      'Use your current skill to land two monthly clients. Great cash flow, but it mostly depends on you.',
    cost: 1200,
    cashFlow: 1800,
    value: 6000,
    timeCost: 3,
    recurringTime: 3,
    skillRequired: 4,
    risk: 'Medium',
    sellability: 'Low',
    lesson: 'High cash flow does not always mean sellable equity.',
  },
  {
    id: 'laundry-route',
    type: 'opportunity',
    title: 'Buy a small laundry pickup route',
    assetName: 'Laundry pickup route',
    description:
      'A tired operator wants out. It is unglamorous, manager-friendly, and only mildly profitable today.',
    cost: 9000,
    cashFlow: 420,
    value: 18000,
    terminalValue: 42000,
    timeCost: 1,
    recurringTime: 1,
    skillRequired: 3,
    risk: 'Low',
    sellability: 'Medium',
    lesson: 'A modest current return can still be attractive when systems and exit value exist.',
  },
  {
    id: 'hvac-leads',
    type: 'opportunity',
    title: 'Local HVAC lead-gen site',
    assetName: 'HVAC lead-gen site',
    description:
      'Build a tiny web property and sell inbound calls to a contractor. Slow start, scalable later.',
    cost: 1800,
    cashFlow: 260,
    value: 5000,
    terminalValue: 22000,
    timeCost: 2,
    recurringTime: 1,
    skillRequired: 3,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'Digital assets can start small but compound if distribution works.',
  },
  {
    id: 'vending',
    type: 'opportunity',
    title: 'Two vending machines',
    assetName: 'Two vending machines',
    description: 'A friend offers two machines in decent locations. Boring, finite, and understandable.',
    cost: 4200,
    cashFlow: 210,
    value: 5200,
    timeCost: 1,
    recurringTime: 1,
    skillRequired: 1,
    risk: 'Low',
    sellability: 'Medium',
    lesson: 'Simple assets teach maintenance, route density, and cash-on-cash returns.',
  },
  {
    id: 'micro-saas',
    type: 'opportunity',
    title: 'Niche scheduling micro-SaaS',
    assetName: 'Scheduling micro-SaaS',
    description:
      'A tiny software tool for independent tutors. No cash flow yet, but the upside is real if you can ship.',
    cost: 3000,
    cashFlow: -120,
    value: 2000,
    terminalValue: 85000,
    timeCost: 4,
    recurringTime: 2,
    skillRequired: 5,
    risk: 'High',
    sellability: 'High',
    lesson: 'Some businesses are option value first and cash flow later.',
  },
  {
    id: 'index-fund',
    type: 'opportunity',
    title: 'Automate boring index investing',
    assetName: 'Index fund position',
    description:
      'Not exciting. Not a story for social media. But it quietly increases assets and future passive income.',
    cost: 1000,
    cashFlow: 4,
    value: 1000,
    timeCost: 0,
    recurringTime: 0,
    skillRequired: 0,
    risk: 'Low',
    sellability: 'High',
    lesson: 'Boring can be excellent when the habit is automatic.',
  },
  {
    id: 'bookkeeping',
    type: 'opportunity',
    title: 'Buy a bookkeeping book',
    assetName: 'Bookkeeping book',
    description:
      'An owner has six sticky clients and no growth plan. Improve process, then hire part-time help.',
    cost: 15000,
    cashFlow: 900,
    value: 21000,
    terminalValue: 48000,
    timeCost: 2,
    recurringTime: 2,
    skillRequired: 4,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'A service book can become an asset when process replaces personality.',
  },
  {
    id: 'mobile-detail',
    type: 'opportunity',
    title: 'Weekend mobile detailing',
    assetName: 'Mobile detailing business',
    description:
      'Start with supplies, hustle, and repeat customers. Cash flow is strong; sellability depends on hiring.',
    cost: 900,
    cashFlow: 650,
    value: 1200,
    timeCost: 3,
    recurringTime: 3,
    skillRequired: 1,
    risk: 'Medium',
    sellability: 'Low',
    lesson: 'A side hustle can be a bridge, not the final asset.',
  },
  {
    id: 'operator-note',
    type: 'opportunity',
    title: 'Small business operator note',
    assetName: 'Operator note',
    description:
      'Lend to a trusted operator expanding a route business. You get monthly payments but limited control.',
    cost: 5000,
    cashFlow: 58,
    value: 5000,
    timeCost: 0,
    recurringTime: 0,
    skillRequired: 2,
    risk: 'Medium',
    sellability: 'Low',
    lesson: 'Passive income can come with counterparty risk.',
  },
  {
    id: 'rental-small',
    type: 'opportunity',
    title: 'Small rental garage bay',
    assetName: 'Rental garage bay',
    description:
      'A tiny real estate deal appears. It is not the whole game, but it can still be a useful asset.',
    cost: 7000,
    cashFlow: 180,
    value: 9000,
    timeCost: 1,
    recurringTime: 1,
    skillRequired: 2,
    risk: 'Low',
    sellability: 'High',
    lesson: 'Real estate belongs in the toolkit, not as the only path.',
  },
  {
    id: 'cleaning-route',
    type: 'opportunity',
    title: 'Buy a tiny cleaning route',
    assetName: 'Tiny cleaning route',
    description: 'Five recurring office clients, messy books, and an owner who wants out.',
    cost: 6500,
    cashFlow: 520,
    value: 9000,
    terminalValue: 26000,
    timeCost: 2,
    recurringTime: 2,
    skillRequired: 2,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'Recurring customers are valuable, but operations decide whether it is an asset or a job.',
  },
  {
    id: 'pressure-washing',
    type: 'opportunity',
    title: 'Start pressure washing weekends',
    assetName: 'Weekend pressure washing',
    description: 'Cheap equipment, fast cash, and very little sellable value until you build a crew.',
    cost: 1400,
    cashFlow: 800,
    value: 1800,
    timeCost: 3,
    recurringTime: 3,
    skillRequired: 1,
    risk: 'Medium',
    sellability: 'Low',
    lesson: 'High cash flow that depends on your weekends is useful but not freedom yet.',
  },
  {
    id: 'boring-dividend-basket',
    type: 'opportunity',
    title: 'Dividend ETF basket',
    assetName: 'Dividend ETF basket',
    description: 'A small automated investment with modest cash yield and high liquidity.',
    cost: 2500,
    cashFlow: 9,
    value: 2500,
    timeCost: 0,
    recurringTime: 0,
    skillRequired: 0,
    risk: 'Low',
    sellability: 'High',
    lesson: 'Liquid assets are easy to own, but small yields require lots of capital.',
  },
  {
    id: 'atm-placement',
    type: 'opportunity',
    title: 'ATM in a busy barbershop',
    assetName: 'Barbershop ATM',
    description: 'The location is strong, but cash loading and downtime are your problem.',
    cost: 5200,
    cashFlow: 260,
    value: 6000,
    terminalValue: 11000,
    timeCost: 1,
    recurringTime: 1,
    skillRequired: 2,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'Semi-passive assets still have operating details.',
  },
  {
    id: 'lawn-crew',
    type: 'opportunity',
    title: 'Partner into a lawn crew',
    assetName: 'Lawn crew partnership',
    description: 'A reliable operator needs capital for equipment. You get a minority cash-flow share.',
    cost: 8000,
    cashFlow: 430,
    value: 8500,
    terminalValue: 18000,
    timeCost: 1,
    recurringTime: 0,
    skillRequired: 3,
    risk: 'Medium',
    sellability: 'Low',
    lesson: 'Operator risk matters when you are not the one doing the work.',
  },
  {
    id: 'online-course',
    type: 'opportunity',
    title: 'Build a tiny online course',
    assetName: 'Online course',
    description: 'Turn a skill into lessons. It takes upfront work and may flop, but it can sell while you sleep.',
    cost: 1100,
    cashFlow: 140,
    value: 3500,
    terminalValue: 16000,
    timeCost: 4,
    recurringTime: 1,
    skillRequired: 5,
    risk: 'High',
    sellability: 'Medium',
    lesson: 'Products can decouple income from hours, but distribution is the hard part.',
  },
  {
    id: 'tax-prep-seasonal',
    type: 'opportunity',
    title: 'Seasonal tax prep book',
    assetName: 'Seasonal tax prep book',
    description: 'A retiring preparer offers a small client list. Cash is seasonal and trust-heavy.',
    cost: 12000,
    cashFlow: 500,
    value: 15000,
    terminalValue: 36000,
    timeCost: 2,
    recurringTime: 2,
    skillRequired: 6,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'Seasonal businesses can be excellent if you manage calendar and client trust.',
  },
  {
    id: 'storage-units',
    type: 'opportunity',
    title: 'Four mini storage units',
    assetName: 'Four mini storage units',
    description: 'A tiny real estate-adjacent asset with low drama and low growth.',
    cost: 18000,
    cashFlow: 510,
    value: 22000,
    terminalValue: 36000,
    timeCost: 1,
    recurringTime: 0,
    skillRequired: 2,
    risk: 'Low',
    sellability: 'High',
    lesson: 'Some assets are boring because the work has already been systematized.',
  },
  {
    id: 'amazon-return-pallets',
    type: 'opportunity',
    title: 'Flip return pallets',
    assetName: 'Return pallet flipping',
    description: 'Big gross margins, chaotic inventory, and lots of sorting in your garage.',
    cost: 1800,
    cashFlow: 700,
    value: 1000,
    timeCost: 4,
    recurringTime: 3,
    skillRequired: 2,
    risk: 'High',
    sellability: 'Low',
    lesson: 'Gross profit is not freedom if the process consumes your nights.',
  },
  {
    id: 'mobile-notary',
    type: 'opportunity',
    title: 'Mobile notary appointments',
    assetName: 'Mobile notary side hustle',
    description: 'Certification, evening appointments, and steady side cash.',
    cost: 650,
    cashFlow: 360,
    value: 900,
    timeCost: 2,
    recurringTime: 2,
    skillRequired: 1,
    risk: 'Low',
    sellability: 'Low',
    lesson: 'A simple side hustle can fund the next asset, even if it is not the destination.',
  },
  {
    id: 'fractional-cfo',
    type: 'opportunity',
    title: 'Fractional CFO retainer',
    assetName: 'Fractional CFO retainer',
    description: 'One small business wants monthly financial strategy, reporting, and cash-flow help. Excellent cash flow, but only for someone with serious finance chops.',
    cost: 2400,
    cashFlow: 2200,
    value: 4000,
    timeCost: 3,
    recurringTime: 3,
    skillRequired: 9,
    risk: 'Medium',
    sellability: 'Low',
    lesson: 'Specialized business expertise can create large cash flow, but craft skill alone is not enough.',
  },
  {
    id: 'royalty-catalog',
    type: 'opportunity',
    title: 'Small royalty catalog',
    assetName: 'Royalty catalog',
    description: 'Buy rights to a niche digital product that pays slowly but requires almost no work.',
    cost: 6000,
    cashFlow: 190,
    value: 7000,
    terminalValue: 10000,
    timeCost: 0,
    recurringTime: 0,
    skillRequired: 3,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'True passive income usually costs more capital or accepts more uncertainty.',
  },
  {
    id: 'boutique-agency',
    type: 'opportunity',
    title: 'Buy a boutique digital agency',
    assetName: 'Boutique digital agency',
    description: 'A 4-person agency with steady retainer clients. Owner is burned out and selling at a fair multiple.',
    cost: 18000,
    cashFlow: 1100,
    value: 22000,
    terminalValue: 52000,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 5,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'Service businesses become real assets when retainers and process replace the founder myth.',
  },
  {
    id: 'podcast-network',
    type: 'opportunity',
    title: 'Niche podcast with sponsors',
    assetName: 'Niche podcast',
    description: 'A small show with 18k loyal listeners and a real ad-sales pipeline. Production is the grind.',
    cost: 4500,
    cashFlow: 380,
    value: 7000,
    terminalValue: 22000,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 5,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'Audience is an asset, but only when the show has a clear reason for sponsors to renew.',
  },
  {
    id: 'small-saas',
    type: 'opportunity',
    title: 'Buy a tiny B2B SaaS',
    assetName: 'Tiny B2B SaaS',
    description: 'A scheduling tool with 220 paying customers and predictable churn. Support tickets every week.',
    cost: 28000,
    cashFlow: 1450,
    value: 38000,
    terminalValue: 130000,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 6,
    risk: 'Medium',
    sellability: 'High',
    lesson: 'Recurring revenue earns rich multiples — when retention is real and the codebase is not on fire.',
  },
  {
    id: 'ecom-brand',
    type: 'opportunity',
    title: 'Automated DTC brand',
    assetName: 'DTC pet-supply brand',
    description: 'A pet-supply brand with one strong SKU and a working ad funnel. Inventory and supplier risk are real.',
    cost: 22000,
    cashFlow: 1100,
    value: 30000,
    terminalValue: 70000,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 6,
    risk: 'Medium',
    sellability: 'High',
    lesson: 'Brand multiples reward repeatable systems, not posts.',
  },
  {
    id: 'multi-location-service',
    type: 'opportunity',
    title: 'Three-location pool service',
    assetName: 'Three-location pool service',
    description: 'Three storefronts, two managers in place, owner ready to retire. You manage the managers.',
    cost: 65000,
    cashFlow: 3200,
    value: 95000,
    terminalValue: 220000,
    timeCost: 4,
    recurringTime: 3,
    skillRequired: 7,
    risk: 'Medium',
    sellability: 'High',
    lesson: 'Multi-location moves you from operator to owner — and you pay for it in management.',
  },
  {
    id: 'paid-newsletter',
    type: 'opportunity',
    title: 'Paid newsletter and community',
    assetName: 'Paid newsletter',
    description: 'A founder-led paid newsletter with 1,200 subscribers at $15/mo. Weekly publishing keeps it alive.',
    cost: 6500,
    cashFlow: 520,
    value: 11000,
    terminalValue: 38000,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 7,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'Paid subscribers are sticky if you ship every week — treat the list like inventory.',
  },
  {
    id: 'publishing-imprint',
    type: 'opportunity',
    title: 'Niche publishing imprint',
    assetName: 'Niche publishing imprint',
    description: 'A catalog of 14 books and 3 courses generating modest royalties. Mostly hands-off, but rights are complex.',
    cost: 32000,
    cashFlow: 1400,
    value: 42000,
    terminalValue: 95000,
    timeCost: 3,
    recurringTime: 1,
    skillRequired: 8,
    risk: 'Medium',
    sellability: 'High',
    lesson: 'Owning rights and distribution beats producing more output.',
  },
  {
    id: 'mature-software',
    type: 'opportunity',
    title: 'Buy a boring mature SaaS',
    assetName: 'Mature invoicing SaaS',
    description: 'A 12-year-old invoicing tool. Slow-moving customer base, low churn, no growth — but stable cash.',
    cost: 90000,
    cashFlow: 3800,
    value: 120000,
    terminalValue: 220000,
    timeCost: 2,
    recurringTime: 1,
    skillRequired: 8,
    risk: 'Low',
    sellability: 'High',
    lesson: 'Boring beats trendy when the cash flow has compounded for a decade without you.',
  },
  {
    id: 'marketplace-flipping',
    type: 'opportunity',
    title: 'Local marketplace flipping',
    assetName: 'Marketplace flipping',
    description: 'Source furniture and tools cheap, resell on Facebook Marketplace. Garage fills up before bank account does.',
    cost: 1500,
    cashFlow: 420,
    value: 2000,
    timeCost: 3,
    recurringTime: 3,
    skillRequired: 3,
    risk: 'Medium',
    sellability: 'Low',
    lesson: 'A side hustle can fund the next asset, but if it stays your job it never becomes an asset.',
  },
  {
    id: 'storage-auctions',
    type: 'opportunity',
    title: 'Storage unit auctions',
    assetName: 'Storage unit auction lots',
    description: 'Bid on abandoned units, sort, resell. Adventurous start, repetitive grind.',
    cost: 3000,
    cashFlow: 380,
    value: 3500,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 3,
    risk: 'High',
    sellability: 'Low',
    lesson: 'Variance hides inside high-margin sourcing — average matters more than the best haul.',
  },
  {
    id: 'etsy-shop',
    type: 'opportunity',
    title: 'Mature Etsy shop, 200 listings',
    assetName: 'Etsy shop',
    description: 'Acquire a niche craft shop with stable orders and a small fulfillment routine.',
    cost: 4500,
    cashFlow: 240,
    value: 5500,
    terminalValue: 14000,
    timeCost: 2,
    recurringTime: 2,
    skillRequired: 3,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'Marketplace assets ride platform algorithms — sellable, but with platform risk baked in.',
  },
  {
    id: 'tutoring-agency',
    type: 'opportunity',
    title: 'Tutoring agency with substitute network',
    assetName: 'Tutoring agency',
    description: 'A small tutoring service with 30 students, two staff tutors, and a manager-friendly process.',
    cost: 7500,
    cashFlow: 600,
    value: 11000,
    terminalValue: 26000,
    timeCost: 2,
    recurringTime: 2,
    skillRequired: 4,
    risk: 'Low',
    sellability: 'Medium',
    lesson: 'Education businesses scale on systems, not personality. Replace yourself early.',
  },
  {
    id: 'handyman-leads',
    type: 'opportunity',
    title: 'Handyman lead-gen with subcontractors',
    assetName: 'Handyman lead-gen',
    description: 'Generate leads, dispatch to vetted handymen, take a margin. Phones ring early.',
    cost: 5000,
    cashFlow: 720,
    value: 8000,
    terminalValue: 18000,
    timeCost: 2,
    recurringTime: 2,
    skillRequired: 4,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'Marketplaces and brokerages capture margin without owning the work.',
  },
  {
    id: 'car-wash-route',
    type: 'opportunity',
    title: 'Mobile car wash route',
    assetName: 'Mobile car wash route',
    description: 'Subscriptions for fleet customers — apartment complexes, dealerships. Equipment is owned.',
    cost: 9000,
    cashFlow: 580,
    value: 12000,
    terminalValue: 22000,
    timeCost: 2,
    recurringTime: 2,
    skillRequired: 4,
    risk: 'Low',
    sellability: 'Medium',
    lesson: 'Subscriptions are the wedge that turns a job into an asset.',
  },
  {
    id: 'boutique-fitness',
    type: 'opportunity',
    title: 'Boutique fitness class business',
    assetName: 'Boutique fitness studio',
    description: 'Lease a small studio, run group classes, members pay monthly. You teach until you hire.',
    cost: 12000,
    cashFlow: 800,
    value: 14000,
    terminalValue: 24000,
    timeCost: 4,
    recurringTime: 3,
    skillRequired: 4,
    risk: 'Medium',
    sellability: 'Low',
    lesson: 'Service businesses are sellable only when the founder is replaceable.',
  },
  {
    id: 'real-estate-birddog',
    type: 'opportunity',
    title: 'Real-estate wholesaler partnership',
    assetName: 'Real-estate wholesaler partnership',
    description: 'Find off-market properties, assign contracts to investors. Cold outreach grind, 1-2 deals a quarter.',
    cost: 2500,
    cashFlow: 950,
    value: 3000,
    timeCost: 3,
    recurringTime: 3,
    skillRequired: 4,
    risk: 'High',
    sellability: 'Low',
    lesson: 'Wholesaling looks fast and free; the customer base is cold leads, not warm referrals.',
  },
  {
    id: 'print-on-demand',
    type: 'opportunity',
    title: 'Print-on-demand catalog',
    assetName: 'Print-on-demand catalog',
    description: 'Niche apparel designs published across multiple platforms. Royalties trickle in once listings rank.',
    cost: 2200,
    cashFlow: 280,
    value: 3000,
    terminalValue: 9000,
    timeCost: 2,
    recurringTime: 1,
    skillRequired: 4,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'Design catalogs compound when you stop chasing trends and let winners ride.',
  },
  {
    id: 'airbnb-rental',
    type: 'opportunity',
    title: 'Single Airbnb property',
    assetName: 'Airbnb studio',
    description: 'A studio in a tourist district. Cleaner is contracted; you handle the listing and pricing.',
    cost: 22000,
    cashFlow: 720,
    value: 30000,
    terminalValue: 55000,
    timeCost: 2,
    recurringTime: 1,
    skillRequired: 5,
    risk: 'Medium',
    sellability: 'High',
    lesson: 'Short-term rentals turn into a job fast — automate cleaning and pricing or it eats weekends.',
  },
  {
    id: 'wedding-photography',
    type: 'opportunity',
    title: 'Wedding photography business',
    assetName: 'Wedding photography business',
    description: 'A booked-out calendar 6 months out, second-shooter network, strong referral pipeline.',
    cost: 6500,
    cashFlow: 1100,
    value: 9000,
    terminalValue: 18000,
    timeCost: 4,
    recurringTime: 3,
    skillRequired: 5,
    risk: 'Medium',
    sellability: 'Low',
    lesson: 'Trust-based services have great cash flow and almost no transferable value.',
  },
  {
    id: 'niche-blog',
    type: 'opportunity',
    title: 'Affiliate niche blog',
    assetName: 'Affiliate niche blog',
    description: 'A 4-year-old SEO site ranking for buyer-intent keywords. Steady traffic, but Google could change everything.',
    cost: 18000,
    cashFlow: 850,
    value: 24000,
    terminalValue: 60000,
    timeCost: 2,
    recurringTime: 1,
    skillRequired: 5,
    risk: 'High',
    sellability: 'High',
    lesson: 'Platform-dependent assets are sellable but the valuation reflects the platform risk.',
  },
  {
    id: 'tax-prep-retainer',
    type: 'opportunity',
    title: 'Year-round tax & advisory retainer',
    assetName: 'Tax & advisory retainer',
    description: 'Three small businesses pay for monthly bookkeeping plus tax prep. Reliable cash, deadline-driven months.',
    cost: 9000,
    cashFlow: 1300,
    value: 13000,
    terminalValue: 32000,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 5,
    risk: 'Low',
    sellability: 'Medium',
    lesson: 'Recurring professional services are the unglamorous workhorse of small business cash flow.',
  },
  {
    id: 'ecom-flip',
    type: 'opportunity',
    title: 'Acquired e-commerce site',
    assetName: 'Acquired e-commerce site',
    description: 'A 3-year-old shop on Empire Flippers. Profitable, but ad accounts and supplier relationships transfer messy.',
    cost: 35000,
    cashFlow: 1600,
    value: 48000,
    terminalValue: 95000,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 6,
    risk: 'Medium',
    sellability: 'High',
    lesson: 'Buying beats building when due diligence catches the rot.',
  },
  {
    id: 'house-flip',
    type: 'opportunity',
    title: 'Single-family flip',
    assetName: 'House flip project',
    description: 'A distressed home in a decent neighborhood. Six months of work, then market it back out.',
    cost: 40000,
    cashFlow: 0,
    value: 65000,
    terminalValue: 75000,
    timeCost: 4,
    recurringTime: 2,
    skillRequired: 6,
    risk: 'High',
    sellability: 'High',
    lesson: 'Flipping is project work, not income — your cash flow is the eventual sale.',
  },
  {
    id: 'event-business',
    type: 'opportunity',
    title: 'Niche annual conference',
    assetName: 'Niche annual conference',
    description: 'A 200-person professional conference with sponsors and ticket revenue. Whole year orbits the date.',
    cost: 14000,
    cashFlow: 950,
    value: 18000,
    terminalValue: 42000,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 6,
    risk: 'High',
    sellability: 'Medium',
    lesson: 'Events have high cash flow once tradition forms, but a single bad year can sink you.',
  },
  {
    id: 'creator-roster',
    type: 'opportunity',
    title: 'Influencer sponsorship roster',
    assetName: 'Creator sponsorship roster',
    description: 'Manage sponsorships for a stable of 8 mid-size creators. You take 20% of every deal.',
    cost: 5000,
    cashFlow: 1200,
    value: 8000,
    terminalValue: 28000,
    timeCost: 4,
    recurringTime: 3,
    skillRequired: 6,
    risk: 'Medium',
    sellability: 'Low',
    lesson: 'Talent agencies are powerful when contracts are sticky and ugly when they are not.',
  },
  {
    id: 'real-estate-syndication',
    type: 'opportunity',
    title: 'Real-estate syndication GP slice',
    assetName: 'Real-estate syndication GP slice',
    description: 'Co-sponsor a small multifamily deal. Fees plus a slice of upside. Years to liquidity.',
    cost: 50000,
    cashFlow: 850,
    value: 60000,
    terminalValue: 180000,
    timeCost: 2,
    recurringTime: 2,
    skillRequired: 7,
    risk: 'High',
    sellability: 'Low',
    lesson: 'Asset-management businesses make money on capital you do not own — and the work is investor relations.',
  },
  {
    id: 'consulting-firm',
    type: 'opportunity',
    title: 'Boutique consultancy with named partners',
    assetName: 'Boutique consultancy',
    description: 'Take over a 3-partner firm. Engagements are 6-month retainers; partners are leaving on a glide path.',
    cost: 80000,
    cashFlow: 4200,
    value: 110000,
    terminalValue: 240000,
    timeCost: 4,
    recurringTime: 3,
    skillRequired: 7,
    risk: 'Medium',
    sellability: 'Medium',
    lesson: 'A partnership has real value only when the founders\' relationships transfer.',
  },
  {
    id: 'course-academy',
    type: 'opportunity',
    title: 'Online education company',
    assetName: 'Online education company',
    description: 'A library of 8 courses, 4 instructors, growing email list. Ads still profitable, support outsourced.',
    cost: 45000,
    cashFlow: 2400,
    value: 60000,
    terminalValue: 135000,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 7,
    risk: 'Medium',
    sellability: 'High',
    lesson: 'Content businesses become assets when the founder\'s face is no longer the product.',
  },
  {
    id: 'property-management',
    type: 'opportunity',
    title: 'Small property management company',
    assetName: 'Property management book',
    description: 'A 110-unit residential management book. Stable margins, demanding tenants, 4 staff to retain.',
    cost: 110000,
    cashFlow: 4200,
    value: 145000,
    terminalValue: 280000,
    timeCost: 3,
    recurringTime: 2,
    skillRequired: 8,
    risk: 'Medium',
    sellability: 'High',
    lesson: 'Property management looks stable from outside; inside it is a parade of small decisions.',
  },
  {
    id: 'ma-advisory',
    type: 'opportunity',
    title: 'M&A advisory boutique',
    assetName: 'M&A advisory boutique',
    description: 'A regional firm closing 4-6 small deals a year. Success-fee revenue is lumpy but enormous.',
    cost: 75000,
    cashFlow: 5200,
    value: 100000,
    terminalValue: 220000,
    timeCost: 4,
    recurringTime: 3,
    skillRequired: 9,
    risk: 'High',
    sellability: 'Medium',
    lesson: 'Lumpy success-fee businesses look great year-over-year and brutal in any single quarter.',
  },
  {
    id: 'search-fund',
    type: 'opportunity',
    title: 'Acquire and operate a small business',
    assetName: 'Acquired small business',
    description: 'Use accumulated capital to acquire and run a $1.5M EBITDA business. The hardest job you have ever had.',
    cost: 250000,
    cashFlow: 12000,
    value: 350000,
    terminalValue: 950000,
    timeCost: 4,
    recurringTime: 3,
    skillRequired: 10,
    risk: 'High',
    sellability: 'High',
    lesson: 'Buying a real business is the FIRE accelerator — and the riskiest swing on the table.',
  },
];
